import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CashDrawerSettings } from "../interfaces/settings.interface";
import axiosClient from "../api/axiosClient";


interface CashDrawerState {
  settings: CashDrawerSettings | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  opening: boolean;
}

const initialState: CashDrawerState = {
  settings: null,
  loading: false,
  saving: false,
  error: null,
  opening: false,
};

export const getCashDrawerSettings = createAsyncThunk(
  "cashDrawer/getSettings",
  async (businessId: string, { rejectWithValue }) => {
    try {
      const res = await axiosClient.get(
        `/businesses/${businessId}/cash-drawer-settings`
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to load cash drawer settings"
      );
    }
  }
);

export const openCashDrawer = createAsyncThunk(
    "cashDrawer/open",
    async (businessId: string, { rejectWithValue }) => {
      try {
        const res = await axiosClient.post(
          `/businesses/${businessId}/cash-drawer/open`
        );
        return res.data;
      } catch (err: any) {
        return rejectWithValue(err.response?.data || "Failed to open cash drawer");
      }
    }
);

export const createCashDrawerSettings = createAsyncThunk(
  "cashDrawer/createSettings",
  async (
    payload: { businessId: string; data: CashDrawerSettings },
    { rejectWithValue }
  ) => {
    try {
      const res = await axiosClient.post(
        `/businesses/${payload.businessId}/cash-drawer-settings`,
        payload.data
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to create cash drawer settings"
      );
    }
  }
);

export const updateCashDrawerSettings = createAsyncThunk(
  "cashDrawer/updateSettings",
  async (
    payload: { businessId: string; data: CashDrawerSettings },
    { rejectWithValue }
  ) => {
    try {
      const res = await axiosClient.put(
        `/businesses/${payload.businessId}/cash-drawer-settings`,
        payload.data
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update cash drawer settings"
      );
    }
  }
);

const settingsSlice = createSlice({
  name: "cashDrawer",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getCashDrawerSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCashDrawerSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(getCashDrawerSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // CREATE
      .addCase(createCashDrawerSettings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createCashDrawerSettings.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload;
      })
      .addCase(createCashDrawerSettings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })

      // UPDATE
      .addCase(updateCashDrawerSettings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateCashDrawerSettings.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload;
      })
      .addCase(updateCashDrawerSettings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })
      //OPEN REGISTER
      .addCase(openCashDrawer.pending, (state) => {
        state.opening = true;
        state.error = null;
      })
      .addCase(openCashDrawer.fulfilled, (state) => {
        state.opening = false;
      })
      .addCase(openCashDrawer.rejected, (state, action) => {
        state.opening = false;
        state.error = action.payload as string;
      });
  },
});

export default settingsSlice.reducer;